import React, {useId} from 'react'
import PropTypes from 'prop-types'
import {Box, List, ListItem, Text} from '@chakra-ui/react'
import {Component} from '@salesforce/commerce-sdk-react/components'
import PDCTitle from '../../../components/pdc-title'
import {useIntl} from 'react-intl'
import Carousel from '../../../components/carousel'

/**
 * PDCAboutStoryLinksBlock is a presentational React component designed for the Page Designer Canvas (PDC) that displays a section with a title, descriptions, and a dynamic list of story link tiles. The component supports both carousel and list layouts, automatically choosing the appropriate display based on the number of tiles and the configured slides to show. It is highly customizable via props for colors, typography, layout, and content, and is responsive to different screen sizes.
 *
 * @component
 * @param {Object} props - The properties object.
 * @param {string} props.title - The main title of the block.
 * @param {string|number} props.titleHeadingLevel - The heading level for the title (e.g., 'h2', 2).
 * @param {string} props.titleFontColor - The color of the title text.
 * @param {string} props.firstDesc - The first description, rendered as HTML.
 * @param {string} props.secondDesc - The second description, rendered as HTML.
 * @param {string} props.backgroundColor - The background color of the block.
 * @param {string} props.textColor - The text color for the block.
 * @param {number} props.smallNbSlidesToShow - Number of slides to show in the carousel on small screens.
 * @param {number} props.displayPriority - Display priority for the tiles.
 * @param {Array} props.regions - Array of region objects, where the first region contains the tiles to display.
 *
 * @returns {JSX.Element} A sectioned layout containing a title, descriptions, and a responsive list or carousel of story link tiles.
 *
 * @example
 * <PDCAboutStoryLinksBlock
 *   title="Our Story"
 *   titleHeadingLevel={2}
 *   titleFontColor="#333"
 *   firstDesc="<p>First description</p>"
 *   secondDesc="<p>Second description</p>"
 *   backgroundColor="#fff"
 *   textColor="#000"
 *   smallNbSlidesToShow={1}
 *   displayPriority={1}
 *   regions={[{ components: [...] }]}
 * />
 */
export const PDCAboutStoryLinksBlock = ({
    title,
    titleHeadingLevel,
    titleFontColor,
    firstDesc,
    secondDesc,
    backgroundColor,
    textColor,
    smallNbSlidesToShow,
    displayPriority,
    regions
}) => {
    const intl = useIntl()

    // Get the tiles from the regions
    let tiles = []
    if (regions) {
        tiles = regions[0].components || []
    }

    // Get the number of slides to show depending on the screen size
    const currentNbSlidesToShow = smallNbSlidesToShow || 1

    const titleId = useId()

    const carouselSettings = {
        dots: false,
        arrows: false,
        infinite: true,
        speed: 500,
        slidesToShow: currentNbSlidesToShow,
        centerMode: true,
        centerPadding: '44px'
    }

    return (
        <Box
            as="section"
            aria-describedby={titleId}
            backgroundColor={backgroundColor}
            color={textColor}
            pt={[80, null, 120]}
            pb={[80, null, 115]}
            px={[0, null, 20]}
            display={'flex'}
            flexDirection={['column', null, 'row']}
        >
            <Box width={['100%', '100%', '50%']} px={['44px', null, 50]}>
                <Box maxWidth={['none', null, 393]}>
                    <PDCTitle
                        title={title}
                        titleHeadingLevel={titleHeadingLevel}
                        titleFontColor={titleFontColor}
                        id={titleId}
                        size={'4xl'}
                    />
                    <Text
                        dangerouslySetInnerHTML={{
                            __html: firstDesc
                        }}
                        variant="eyeBrowSmall"
                        mt={[20, null, 24]}
                    />
                    <Text
                        dangerouslySetInnerHTML={{
                            __html: secondDesc
                        }}
                        variant="bodyLarge2"
                        color={'darkGray'}
                        mt={[34, null, '44px']}
                        mb={['44px', null, 0]}
                    />
                </Box>
            </Box>
            {/* Display a carousel only if there are more tiles than the number of slides to show */}
            <Box
                display={[
                    tiles && tiles.length > currentNbSlidesToShow ? 'block' : 'none',
                    null,
                    'none'
                ]}
            >
                <Carousel
                    settings={carouselSettings}
                    label={intl.formatMessage({
                        id: 'about.story.links.block.carousel.label',
                        defaultMessage: 'About story links block carousel'
                    })}
                    // loadingRatio={'13 / 17'}
                    nbSlidesToShowByBreakpoint={{
                        base: smallNbSlidesToShow,
                        sm: smallNbSlidesToShow
                    }}
                >
                    {tiles.map((tile, index) => {
                        tile.displayPriority = displayPriority
                        tile.ratio = '13 / 17'
                        return <Component key={tile?.id || index} component={tile} />
                    })}
                </Carousel>
            </Box>
            <List
                listStyleType="none"
                display={[
                    tiles && tiles.length > currentNbSlidesToShow ? 'none' : 'block',
                    null,
                    'flex'
                ]}
                flexWrap={'wrap'}
                justifyContent="center"
                width={['100%', '100%', '50%']}
                rowGap={24}
                px={[39, null, 0]}
            >
                {tiles.map((tile, index) => {
                    tile.nbSlides = currentNbSlidesToShow
                    tile.displayPriority = displayPriority
                    tile.ratio = index < 2 ? '13 / 17' : ['13 / 17', null, '139 / 90']
                    return (
                        <ListItem
                            key={tile?.id || index}
                            flexBasis={['100%', null, index < 2 ? '50%' : '100%']}
                            mb={[19, null, 0]}
                        >
                            <Component component={tile} />
                        </ListItem>
                    )
                })}
            </List>
        </Box>
    )
}

PDCAboutStoryLinksBlock.propTypes = {
    title: PropTypes.string.isRequired,
    titleHeadingLevel: PropTypes.string,
    titleFontColor: PropTypes.string,
    firstDesc: PropTypes.string,
    secondDesc: PropTypes.string,
    backgroundColor: PropTypes.string,
    textColor: PropTypes.string,
    smallNbSlidesToShow: PropTypes.number,
    mediumNbSlidesToShow: PropTypes.number,
    largeNbSlidesToShow: PropTypes.number,
    xlargeNbSlidesToShow: PropTypes.number,
    xxlargeNbSlidesToShow: PropTypes.number,
    displayPriority: PropTypes.bool,
    regions: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.string,
            components: PropTypes.arrayOf(
                PropTypes.shape({
                    data: PropTypes.object,
                    id: PropTypes.string,
                    typeId: PropTypes.string
                })
            )
        })
    )
}
